import { Accessor, createSignal, onMount, Setter } from "solid-js";
import { debounce } from "@solid-primitives/scheduled";
import {
  SimulateFunction,
  useDenominationGridStateV2,
} from "./useDenominationGridState";
import { VoucherProduct } from "~/server/types/brand";

export type DenominationGridStateV2 = ReturnType<typeof useDenominationsGridV2>;

export type CtaState = "DEFAULT" | "LOADING" | "DISABLED";

export type DenominationPropsV2 = {
  initialVariantId: string | null;
  initialConsumeCoins: boolean;

  brand: VoucherProduct;
  isCoinsOnly: boolean;
  coinsBalance: number;
  isRewardTypePostPaid: boolean;

  allowRewardCoins: boolean;
  showHubbleCoinRewards: boolean;

  /** Function to simulate transactions */
  simulateFunction: SimulateFunction;

  /** Function to initiate order - now receives internal state */
  initiateOrder: (props: {
    amount: number;
    variantId: string | null;
    consumeCoins: boolean;
    denominationDetails?: { denomination: number; quantity: number }[];
  }) => void;

  ctaState?: Accessor<CtaState>;
  setCtaState?: Setter<CtaState>;
};

export function useDenominationsGridV2(props: DenominationPropsV2) {
  const getDefaultAmount = () => {
    let defaultAmount = props.brand.amountConditions.defaultAmount;
    if (
      props.isCoinsOnly &&
      props.coinsBalance > 0 &&
      props.coinsBalance < defaultAmount
    ) {
      defaultAmount = props.coinsBalance;
    }
    return defaultAmount;
  };

  const defaultAmount = getDefaultAmount();

  // Extract denomination mode config from brand
  const onlySingleDenominationAllowed =
    props.brand.voucherProductProviderMetadata?.onlySingleDenominationAllowed ??
    false;
  const maxVouchersPerOrder =
    props.brand.restrictions?.maxNumberOfVouchersPerOrder ?? 10;

  const isFixedDenominationBrand =
    props.brand.amountConditions.denominations != null;

  const internalState = useDenominationGridStateV2({
    externalProps: props,
    // override the initial amount with the default amount
    initialAmount: defaultAmount,
    onlySingleDenominationAllowed,
    maxVouchersPerOrder,
    isFixedDenominationBrand,
  });

  function enoughCoinsAvailable() {
    if (!props.isCoinsOnly) {
      return true;
    }

    return (
      props.coinsBalance >=
      (internalState.simulationStore.response?.totalRequiredCoins ??
        internalState.selectedAmount())
    );
  }

  const customAmountDefaultDenominationsList = [
    100, 500, 1000, 2000, 5000,
  ].filter(
    (denomination) =>
      denomination >= props.brand.amountConditions.minAmount &&
      denomination <= props.brand.amountConditions.maxAmount
  );

  const customAmountInputDebounce = debounce((customAmount: number) => {
    if (isNaN(customAmount)) return;
    internalState.setSelectedAmount(customAmount);
  }, 300);

  const isVariantBrand = props.brand.variantDetailedResponse != null;

  let isCustomDenominationAllowed =
    !isFixedDenominationBrand && !isVariantBrand;

  let denominationList =
    props.brand.amountConditions.denominations ??
    customAmountDefaultDenominationsList;

  if (props.isCoinsOnly) {
    denominationList = filterOutUnnecessaryDenominations({
      coinsBalance: props.coinsBalance,
      denominations: denominationList,
      minRedemptionAmount: props.brand.amountConditions.minAmount,
    });
  }

  function safeToShowOnlyOneDenomination(coinsBalance: number) {
    if (!props.isCoinsOnly) {
      return false;
    }

    if (isFixedDenominationBrand || isVariantBrand) {
      return false;
    }

    if (
      coinsBalance < props.brand.amountConditions.minAmount ||
      coinsBalance > props.brand.amountConditions.maxAmount
    ) {
      return false;
    }

    return true;
  }

  let columnsCount = denominationList.length <= 4 ? 2 : 3;

  // if the default amount is present and not avaliable in the denominationList then show it in the custom input field
  const defaultAmountInCustomInput =
    isCustomDenominationAllowed && !denominationList.includes(defaultAmount)
      ? defaultAmount
      : null;

  function filterOutUnnecessaryDenominations(data: {
    coinsBalance: number;
    denominations: number[];
    minRedemptionAmount: number;
  }) {
    if (data.coinsBalance < data.minRedemptionAmount) {
      return data.denominations;
    }

    if (isFixedDenominationBrand) {
      // no need to filter out denominations for fixed denomination brands
      return data.denominations;
    }

    // filter out denominations greater than available coins
    return data.denominations.filter(
      (denomination) => denomination <= data.coinsBalance
    );
  }

  onMount(() => {
    if (defaultAmountInCustomInput !== null) {
      setCustomSelected(true);
      document.getElementById("custom-amount-input")?.focus();
      internalState.setSelectedAmount(defaultAmountInCustomInput);
    }
    if (props.isCoinsOnly && !internalState.selectedAmount()) {
      internalState.setSelectedAmount(defaultAmount);
    }
  });

  const [customSelected, setCustomSelected] = createSignal<boolean>(
    isCustomDenominationAllowed &&
      customAmountDefaultDenominationsList.length == 0
  );

  return {
    customSelected,
    setCustomSelected,
    customAmountInputDebounce,
    isFixedDenominationBrand,
    customAmountDefaultDenominationsList,
    defaultAmount,
    isVariantBrand,
    externalProps: props,
    enoughCoinsAvailable,
    columnsCount,
    defaultAmountInCustomInput,
    safeToShowOnlyOneDenomination,
    denominationList,
    isCustomDenominationAllowed,
    internalState,
    onlySingleDenominationAllowed,
    maxVouchersPerOrder,
  };
}

export default useDenominationsGridV2;
